import { compareAsc, format, parseISO, startOfDay } from "date-fns";

import { Session } from "../../../../types";

/**
 * Cap on the number of session dates that can be selected at once in a
 * SessionDatePicker, to keep downstream requests (e.g. Analysis Builder's
 * /ai/upload_data) from timing out on very large date selections.
 */
export const MAX_SELECTABLE_SESSION_DATES = 3;

/**
 * Convert selected dates to session IDs.
 * Sessions matching any of the given dates are included.
 */
export function getSessionIdsFromDates(
  dates: Date[],
  sessions: Session[],
): string[] {
  const sessionIds = new Set<string>();
  dates.forEach((date) => {
    const dateStr = format(startOfDay(date), "yyyy-MM-dd");
    sessions.forEach((session) => {
      if (session.sessionDate) {
        try {
          const sessionDateStr = format(
            startOfDay(parseISO(session.sessionDate)),
            "yyyy-MM-dd",
          );
          if (sessionDateStr === dateStr) {
            sessionIds.add(session.id);
          }
        } catch {
          // Invalid date, skip
        }
      }
    });
  });
  return [...sessionIds];
}

/**
 * Convert session IDs to unique dates.
 */
export function getDatesFromSessionIds(
  sessionIds: string[],
  sessions: Session[],
): Date[] {
  if (!sessionIds?.length) return [];
  const uniqueDates = new Set<string>();
  return sessionIds
    .map((sessionId) => {
      const session = sessions.find((s) => s.id === sessionId);
      if (!session?.sessionDate) return null;
      const dateKey = session.sessionDate.split("T")[0];
      if (!uniqueDates.has(dateKey)) {
        uniqueDates.add(dateKey);
        return parseISO(session.sessionDate);
      }
      return null;
    })
    .filter((date): date is Date => date !== null);
}

/**
 * Format a list of dates for display in the date picker button.
 * @param dates - Dates to display
 * @param emptyPlaceholder - Text when no dates selected
 * @param pluralThreshold - When exceed this count, show "N dates selected" (default 2)
 */
export function formatDateSelectionDisplay(
  dates: Date[],
  emptyPlaceholder: string,
  pluralThreshold = 2,
): string {
  if (dates.length === 0) return emptyPlaceholder;
  if (dates.length > pluralThreshold) return `${dates.length} dates selected`;
  const sortedDates = [...dates].sort(compareAsc);
  return sortedDates.map((date) => format(date, "M/d/yyyy")).join(", ");
}
